/**
 * Permit API
 *
 * Outbound Works-Lite → CORE permit lifecycle calls. Each response is
 * whatever the backend returned; the button state UI is driven by the
 * subsequent inbound update that lands on job-detail via polling.
 *
 * Endpoints mounted at /works-lite/work-orders in the backend router.
 */

import { apiClient } from '@/lib/api';
import { ApiError } from '@/lib/api/types/errors';
import type {
  OutboundExtendPermitBody,
  OutboundRequestPermitBody,
  OutboundStartPermitBody,
  OutboundStopPermitBody,
} from '@skewb-works/contracts';

export interface PermitActionResult {
  jobId: number;
  permitId?: number;
  success: boolean;
  message: string;
}

/**
 * CWL-145 — the permit endpoints answer HTTP 200 with {success:false} when CORE
 * rejected the action (the outbound service catches its own errors and the route
 * always returns 200). React Query therefore treated a rejection as a success and
 * the global handler fired the static "sent to SAP" toast, which is how permit
 * extension requests silently failed to reach SAP for a month.
 *
 * Rejecting here routes the backend's own message through handleMutationError →
 * showErrorToastFromError. ApiError carries ErrorType.API, which that switch
 * renders via its default branch (the message verbatim) and which the mutation
 * retry predicate excludes — so the action is reported, not re-sent.
 */
async function unwrapPermitResult(
  endpoint: string,
  request: Promise<PermitActionResult>,
): Promise<PermitActionResult> {
  const result = await request;
  if (!result.success) {
    throw new ApiError(
      result.message || 'The permit request could not be sent to CORE.',
      200,
      endpoint,
    );
  }
  return result;
}

export function requestPermit(body: OutboundRequestPermitBody): Promise<PermitActionResult> {
  const endpoint = '/works-lite/work-orders/outbound/request-permit';
  return unwrapPermitResult(endpoint, apiClient.post(endpoint, body));
}

export function startPermit(body: OutboundStartPermitBody): Promise<PermitActionResult> {
  const endpoint = '/works-lite/work-orders/outbound/start-permit';
  return unwrapPermitResult(endpoint, apiClient.post(endpoint, body));
}

export function stopPermit(body: OutboundStopPermitBody): Promise<PermitActionResult> {
  const endpoint = '/works-lite/work-orders/outbound/stop-permit';
  return unwrapPermitResult(endpoint, apiClient.post(endpoint, body));
}

export function extendPermit(body: OutboundExtendPermitBody): Promise<PermitActionResult> {
  const endpoint = '/works-lite/work-orders/outbound/extend-permit';
  return unwrapPermitResult(endpoint, apiClient.post(endpoint, body));
}
